import Swal from "sweetalert2";
import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  PlusCircle,
  PencilSquare,
  Trash,
  Building,
  People,
  Gear,
  GeoAlt,
  Phone,
  Envelope,
  CreditCard,
  Clock,
  CheckCircle,
  BoxSeam,
  CalendarCheck,
} from "react-bootstrap-icons";
import "react-phone-input-2/lib/bootstrap.css";
import axios from "axios";
import RegisterCompany from "./RegisterCompany";
import ManageServices from "./ManageServices";
import ManageEmployees from "./ManageEmployees";
import VerifyBusiness from "./VerifyBusiness";
import InventoryManager from "../components/InventoryManager";
import AttendanceModal from "../components/AttendanceModal";
import { QRCodeSVG } from "qrcode.react";
import API_BASE_URL from "../config";
import html2canvas from "html2canvas";
import { useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";

// Custom CSS for professional card styling and select effect
const customStyles = `
  .professional-card {
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    background-color: #fff;
  }
  .professional-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
  .professional-card.selected {
    border: 2px solid #0066cc;
    background-color: #f8f9fa;
  }
  .professional-card .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
  }
  .professional-card .card-text {
    font-size: 0.9rem;
    color: #4a4a4a;
  }
  .action-buttons-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    padding: 0.5rem;
  }
  .action-button {
    width: 100%;
    max-width: 120px;
    border-radius: 8px !important;
    padding: 6px 10px !important;
    font-size: 0.85rem !important;
    transition: background-color 0.2s ease-in-out;
  }
  .action-button:hover {
    background-color: #e9ecef !important;
  }
  .manage-company-phone-container {
    display: flex;
    align-items: center;
  }
  .manage-company-phone-input {
    width: 100%;
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    border: 1px solid #ced4da;
    height: 38px;
  }
  .manage-company-phone-button {
    border-radius: 0.25rem 0 0 0.25rem;
    border: 1px solid #ced4da;
    border-right: none;
  }
`;

// Inject custom styles into the document
const styleSheet = document.createElement("style");
styleSheet.innerText = customStyles;
document.head.appendChild(styleSheet);

const ManageCompany = ({ userId }) => {
  const [razorpayScriptLoaded, setRazorpayScriptLoaded] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null); // Track selected card

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const [branches, setBranches] = useState([]);
  const [isInternational, setIsInternational] = useState(false);
  const [branchForm, setBranchForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    phone: "",
    landmark: "",
  });
  const [branchError, setBranchError] = useState(null);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchLoading, setBranchLoading] = useState(false);

  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    owner_name: "",
    company_email: "",
    phone: "+91", // Default to +91
    website: "",
    estd: "",
    pan_number: "",
    gst_number: "",
    city: "",
    state: "",
    country: "",
    address: "",
    landmark: "",
    postal_code: "",
    category_id: "",
    subcategory_id: "",
    logo: null,
  });
  const [editError, setEditError] = useState(null);

  const [packages, setPackages] = useState([]);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);

  const qrCodeRef = useRef(null);
  const location = useLocation();
  // NEW STATE – toggle value (boolean)
  const [acceptPayments, setAcceptPayments] = useState(
    !!editForm.accept_online_payments // will be 1 or 0 from API
  );

  useEffect(() => {
    fetchCompanies();
    fetchPackages();

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => setRazorpayScriptLoaded(true);
    document.body.appendChild(script);

    const params = new URLSearchParams(location.search);
    if (params.get("success")) {
      Swal.fire({
        icon: "success",
        title: "Payment Successful!",
        text: "Subscription updated.",
        timer: 3000,
      });
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("payment") === "success") {
      Swal.fire({
        icon: "success",
        title: "Stripe Payment Successful!",
        text: "Your subscription is now active.",
        timer: 3000,
      });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("payment") === "cancel") {
      Swal.fire({
        icon: "warning",
        title: "Payment Cancelled",
        text: "You can try again anytime.",
        timer: 3000,
      });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error") || params.get("fail")) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: params.get("error") || "Please try again.",
        timer: 3000,
      });
    }
  }, [location]);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/payments/packages`);
      setPackages(res.data);
    } catch (err) {
      console.error("Failed to load packages", err);
    }
  };

  // SweetAlert when toggle turns ON
  const handleToggleChange = async (e) => {
    const newVal = e.target.checked;
    if (newVal) {
      const result = await Swal.fire({
        title: "Online Payments",
        html: `
          <p>TickTickGo.in will deduct:</p>
          <ul class="text-start mb-0">
            <li><strong>₹11</strong> (INR platform fee)</li>
            <li><strong>Payment-gateway charges</strong> (12-18%)</li>
          </ul>
          <p class="mt-2"><strong>Your prices must be inclusive of GST.</strong></p>
          <p class="mt-2 mb-0">Final amount you receive = Price – (Platform fee + Gateway fee)</p>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "I Understand, Enable",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
      });

      if (result.isConfirmed) {
        setAcceptPayments(true);
        setEditForm((prev) => ({
          ...prev,
          accept_online_payments: 1,
        }));
      } else {
        // user cancelled – keep toggle OFF
        e.target.checked = false;
      }
    } else {
      // turning OFF – just update state
      setAcceptPayments(false);
      setEditForm((prev) => ({
        ...prev,
        accept_online_payments: 0,
      }));
    }
  };

  const openSubscribeModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setSelectedPackageId("");
    setIsInternational(false); // ← NEW
    setShowSubscribeModal(true);
  };
  const openInventoryModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Optional: for highlighting
    setShowInventoryModal(true);
  };

  const closeInventoryModal = () => {
    setShowInventoryModal(false);
    setSelectedCompany(null);
  };
  const openAttendanceModal = (company) => {           
  setSelectedCompany(company);
  setSelectedCardId(company.id);
  setShowAttendanceModal(true);
};

  const handleStripeSubscribe = async () => {
    if (!selectedPackageId) return;

    setSubscribeLoading(true);
    try {
      const selectedPackage = packages.find(
        (p) => p.id === parseInt(selectedPackageId)
      );
      const totalInr = Number(selectedPackage.total_amount);
      const totalUsd = Math.round((totalInr / 88) * 100); // in cents

      const res = await axios.post(`${API_BASE_URL}/payments/initiate-stripe`, {
        companyId: selectedCompany.id,
        packageId: selectedPackageId,
        totalAmountUsd: totalUsd,
        currency: "usd",
      });

      window.location.href = res.data.sessionUrl;
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Stripe Failed",
        text: err.response?.data?.message || "Could not initiate payment",
      });
    } finally {
      setSubscribeLoading(false);
    }
  };
  const handleSubscribe = async () => {
    if (!selectedPackageId) {
      Swal.fire({ icon: "warning", title: "Select a package" });
      return;
    }
    setSubscribeLoading(true);
    try {
      const selectedPackage = packages.find(
        (pkg) => pkg.id === parseInt(selectedPackageId)
      );
      if (!selectedPackage) {
        throw new Error("Selected package not found");
      }
      const totalAmount = selectedPackage.total_amount * 100;

      const res = await axios.post(`${API_BASE_URL}/payments/initiate`, {
        companyId: selectedCompany.id,
        packageId: selectedPackageId,
        totalAmount: totalAmount,
      });

      if (!razorpayScriptLoaded) {
        throw new Error("Razorpay script not loaded");
      }

      const options = {
        key: process.env.RAZORPAY_KEY_ID,
        amount: res.data.amount,
        currency: res.data.currency,
        name: "TickTickGo",
        description: `Subscription for ${selectedCompany.name}`,
        order_id: res.data.orderId,
        handler: function (response) {
          axios
            .post(`${API_BASE_URL}/payments/callback`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              paymentId: res.data.paymentId,
            })
            .then(() => {
              window.location.href = `${window.location.pathname}?success=1&companyId=${selectedCompany.id}`;
            })
            .catch((err) => {
              Swal.fire({
                icon: "error",
                title: "Payment Error",
                text: err.response?.data?.message || "Verification failed",
              });
            });
        },
        prefill: {
          name: selectedCompany.owner_name,
          email: selectedCompany.company_email,
          contact: selectedCompany.phone,
        },
        notes: {
          company_id: selectedCompany.id,
        },
        theme: {
          color: "#0066cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to initiate payment",
        text: err.response?.data?.message || "Unknown error",
      });
    } finally {
      setSubscribeLoading(false);
    }
  };

  const openHistoryModal = async (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/payments/history/${company.id}`
      );
      setPaymentHistory(res.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to load history",
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setEditForm({
      name: company.name || "",
      owner_name: company.owner_name || "",
      company_email: company.company_email || "",
      phone: company.phone || "+91",
      website: company.website || "",
      estd: company.estd || "",
      pan_number: company.pan_number || "",
      gst_number: company.gst_number || "",
      city: company.city || "",
      state: company.state || "",
      country: company.country || "",
      postal_code: company.postal_code || "",
      address: company.address || "",
      landmark: company.landmark || "",
      category_id: company.category_id || "",
      subcategory_id: company.subcategory_id || "",
      check_in_time: company.check_in_time || "14:00",
      check_out_time: company.check_out_time || "12:00",
      logo: null,
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setEditForm({ ...editForm, logo: files[0] });
    } else {
      setEditForm({ ...editForm, [name]: value });
    }
    if (name === "category_id") {
      setEditForm((prev) => ({ ...prev, subcategory_id: "" }));
      fetchSubcategories(value);
    }
  };

  const validateEditForm = () => {
    if (!editForm.name.trim()) return "Company name is required";
    if (!editForm.owner_name.trim()) return "Owner name is required";
    if (!editForm.company_email.match(/^\S+@\S+\.\S+$/))
      return "Valid company email required";
    if (
      !editForm.gst_number.match(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
      )
    )
      return "Valid GST number required (e.g., 22AAAAA0000A1Z5)";
    if (!editForm.category_id) return "Category is required";
    if (!editForm.subcategory_id) return "Subcategory is required";
    if (!/^\+\d{8,15}$/.test(editForm.phone)) {
      return "Please enter a valid phone number with country code.";
    }
    return null;
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setEditError(null);

    const validationError = validateEditForm();
    if (validationError) {
      setEditError(validationError);
      return;
    }

    try {
      const formData = new FormData();
      Object.entries(editForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined) formData.append(key, value);
      });

      await axios.post(
        `${API_BASE_URL}/companies/${selectedCompany.id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Company updated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
      setShowEditModal(false);
      setSelectedCardId(null); // Clear selected card after update
      fetchCompanies();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.response?.data?.message || "Failed to update company",
      });
    }
  };

  const handleDeleteCompany = async (companyId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this! This will delete the company and all related data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/companies/${companyId}`);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Company has been deleted.",
        timer: 2000,
        showConfirmButton: false,
      });
      setSelectedCardId(null); // Clear selected card
      fetchCompanies();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.response?.data?.message || "Failed to delete company",
      });
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/companies`);
      const data = res.data || [];
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("Failed to load companies");
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load companies",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async (companyId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/branches`
      );
      const data = res.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch branches", err);
      setBranches([]);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      setEditForm({
        ...selectedCompany,
        phone: selectedCompany.phone || "+91",
        logo: null,
      });
      fetchCategories();
      if (selectedCompany.category_id) {
        fetchSubcategories(selectedCompany.category_id);
      }
      setAcceptPayments(!!selectedCompany.accept_online_payments);
    }
  }, [selectedCompany]);

  const handleBranchChange = (e) => {
    const { name, value } = e.target;
    setBranchForm({ ...branchForm, [name]: value });
  };

  const resetBranchForm = () => {
    setBranchForm({
      name: "",
      address: "",
      city: "",
      state: "",
      country: "",
      phone: "",
      landmark: "",
    });
    setEditingBranch(null);
    setBranchError(null);
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    setBranchError(null);

    if (!branchForm.name.trim())
      return setBranchError("Branch name is required");
    if (!branchForm.city.trim()) return setBranchError("City is required");
    if (!branchForm.state.trim()) return setBranchError("State is required");
    if (!branchForm.country.trim())
      return setBranchError("Country is required");

    setBranchLoading(true);
    try {
      if (editingBranch) {
        await axios.put(
          `${API_BASE_URL}/branches/${editingBranch.id}`,
          branchForm
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/companies/${selectedCompany.id}/branches`,
          branchForm
        );
      }
      await fetchBranches(selectedCompany.id);
      resetBranchForm();
      Swal.fire({
        icon: "success",
        title: editingBranch ? "Updated!" : "Added!",
        text: `Branch ${editingBranch ? "updated" : "added"} successfully`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      setBranchError(err.response?.data?.message || "Failed to save branch");
    } finally {
      setBranchLoading(false);
    }
  };

  const handleDeleteBranch = async (branchId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/branches/${branchId}`);
      fetchBranches(selectedCompany.id);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Branch has been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete branch",
      });
    }
  };

  const openBranchModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    fetchBranches(company.id);
    setShowBranchModal(true);
  };

  const openServiceModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setShowServiceModal(true);
  };

  const openEmployeeModal = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setShowEmployeeModal(true);
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/categories/${categoryId}/subcategories`
      );
      setSubcategories(res.data || []);
    } catch (err) {
      console.error("Failed to load subcategories", err);
    }
  };

  const generateQRCode = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setShowQRModal(true);
  };

  const handleVerifyCompany = (company) => {
    setSelectedCompany(company);
    setSelectedCardId(company.id); // Set selected card
    setShowVerifyModal(true);
  };

  const handleDownload = () => {
    const qrCodeContainer = document.createElement("div");
    qrCodeContainer.style.position = "absolute";
    qrCodeContainer.style.top = "-9999px";
    qrCodeContainer.style.backgroundColor = "#ffffff";
    qrCodeContainer.style.width = "170px";
    qrCodeContainer.style.height = "210px";
    qrCodeContainer.style.padding = "10px";

    qrCodeContainer.innerHTML = qrCodeRef.current.outerHTML;

    const centerLogo = document.createElement("img");
    centerLogo.src = "/favicon-32x32.png";
    centerLogo.alt = "Center Logo";
    centerLogo.style.position = "absolute";
    centerLogo.style.top = "40%";
    centerLogo.style.left = "50%";
    centerLogo.style.transform = "translate(-50%, -50%)";
    centerLogo.style.height = "24px";
    centerLogo.style.width = "24px";
    qrCodeContainer.appendChild(centerLogo);

    const footerLogo = document.createElement("img");
    footerLogo.src = "/logo.png";
    footerLogo.alt = "Footer Logo";
    footerLogo.style.marginTop = "10px";
    footerLogo.style.display = "block";
    footerLogo.style.marginLeft = "auto";
    footerLogo.style.marginRight = "auto";
    footerLogo.style.height = "30px";
    qrCodeContainer.appendChild(footerLogo);

    document.body.appendChild(qrCodeContainer);

    const logoPromise = new Promise((resolve) => (centerLogo.onload = resolve));
    const footerPromise = new Promise(
      (resolve) => (footerLogo.onload = resolve)
    );

    Promise.all([logoPromise, footerPromise])
      .then(() => {
        html2canvas(qrCodeContainer, {
          scale: 4,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        })
          .then((canvas) => {
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png", 1.0);
            link.download = `qrcode_${selectedCompany?.slug}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            document.body.removeChild(qrCodeContainer);
          })
          .catch((error) => {
            console.error("Error generating QR code image:", error);
            document.body.removeChild(qrCodeContainer);
          });
      })
      .catch((error) => {
        console.error("Error loading logos:", error);
        document.body.removeChild(qrCodeContainer);
      });
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 className="mb-0 fw-bold">
            <Building className="me-2" /> Manage Companies
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="d-flex align-items-center gap-1 px-3 py-2 rounded-3"
          >
            <PlusCircle /> Create Company
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4 rounded-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading companies...
        </div>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {companies.map((c) => (
            <Col key={c.id}>
              <Card
                className={`professional-card h-100 d-flex flex-column ${
                  selectedCardId === c.id ? "selected" : ""
                }`}
              >
                <Card.Body className="p-4 flex-grow-1">
                  {/* ---------- 50 / 25 / 25 LAYOUT ---------- */}
                  <Row className="h-100 g-3 align-items-start">
                    {/* ==== 50% – INFO ==== */}
                    <Col xs={12} lg={6}>
                      <Card.Title className="mb-3">
                        <Button
                          variant="link"
                          className="p-0 text-start text-decoration-none fw-bold"
                          onClick={() => openEditModal(c)}
                        >
                          <PencilSquare className="me-2" size={16} />
                          {c.name}
                        </Button>
                      </Card.Title>

                      <Card.Text>
                        <div className="d-flex align-items-center mb-2">
                          <Envelope className="me-2 text-muted" size={16} />
                          <span>{c.company_email}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <Phone className="me-2 text-muted" size={16} />
                          <span>{c.phone}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Package:</strong> {c.package_name || "None"}
                        </div>
                        <div className="mb-2">
                          <strong>Expiration:</strong>{" "}
                          {c.expiration_date
                            ? new Date(c.expiration_date).toLocaleDateString()
                            : "N/A"}
                        </div>

                        <div className="mb-3">
                          <strong>Branches:</strong>{" "}
                          {c.branches && c.branches.length > 0 ? (
                            <>
                              <strong>{c.branches.length}</strong> branch
                              {c.branches.length > 1 ? "es" : ""}
                              <div className="small text-muted mt-1">
                                {c.branches.slice(0, 2).map((b) => (
                                  <div key={b.id}>
                                    {b.name} ({b.city})
                                  </div>
                                ))}
                                {c.branches.length > 2 &&
                                  `+${c.branches.length - 2} more`}
                              </div>
                            </>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </div>

                        <div>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View QR Code</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="px-3 py-1 rounded-3"
                              onClick={() => generateQRCode(c)}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                                className="me-1"
                              >
                                <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2zm4 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V5zm1 0h2v2h-2V5zm-4 4a1 1 0 0 1 1-1h2a1 1 0 1 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-2zm1 0h2v2h-2v-2zm8-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V5zm1 0h2v2h-2V5zm0 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-2zm1 0h2v2h-2v-2z" />
                              </svg>
                              QR Code
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </Card.Text>
                    </Col>

                    {/* ==== 25% – BUTTON GROUP 1 (4 buttons) ==== */}
                    <Col xs={6} lg={3} className="d-flex flex-column gap-2">
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Manage Inventory</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openInventoryModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <BoxSeam size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Inventory</span>
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Manage Employees</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openEmployeeModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <People size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Employees</span>
                        </Button>
                      </OverlayTrigger>
{/* === NEW ATTENDANCE BUTTON === */}
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Mark Attendance</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openAttendanceModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <CalendarCheck size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Attendance</span>
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Manage Branches</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-info"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openBranchModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <GeoAlt size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Branches</span>
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Verify Business</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => handleVerifyCompany(c)}
                          disabled={c.is_verified}
                          style={{ minHeight: "38px" }}
                        >
                          <CheckCircle
                            size={14}
                            className="me-1 flex-shrink-0"
                          />
                          <span className="text-nowrap">Verify</span>
                        </Button>
                      </OverlayTrigger>
                    </Col>

                    {/* ==== 25% – BUTTON GROUP 2 (4 buttons) ==== */}
                    <Col xs={6} lg={3} className="d-flex flex-column gap-2">
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Manage Services</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openServiceModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <Gear size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Services</span>
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Subscribe/Renew</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-warning"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openSubscribeModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <CreditCard
                            size={14}
                            className="me-1 flex-shrink-0"
                          />
                          <span className="text-nowrap">Subscribe</span>
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Payment History</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => openHistoryModal(c)}
                          style={{ minHeight: "38px" }}
                        >
                          <Clock size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">History</span>
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Delete Company</Tooltip>}
                      >
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="action-button w-100 d-flex align-items-center px-2 py-1"
                          onClick={() => handleDeleteCompany(c.id)}
                          style={{ minHeight: "38px" }}
                        >
                          <Trash size={14} className="me-1 flex-shrink-0" />
                          <span className="text-nowrap">Delete</span>
                        </Button>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <PlusCircle className="me-2" /> Create Company
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RegisterCompany />
        </Modal.Body>
      </Modal>

      <Modal
        show={showBranchModal}
        onHide={() => setShowBranchModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <GeoAlt className="me-2" /> Manage Branches —{" "}
            {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany ? (
            <>
              <h5 className="mb-3 fw-bold">Existing Branches</h5>
              {branches.length === 0 ? (
                <Alert variant="info" className="rounded-3">
                  No branches yet. Add your first branch below!
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>City</th>
                        <th>Phone</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map((b) => (
                        <tr key={b.id}>
                          <td>{b.name}</td>
                          <td>{b.city}</td>
                          <td>{b.phone || "—"}</td>
                          <td className="text-center">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="me-1 rounded-3"
                              onClick={() => {
                                setEditingBranch(b);
                                setBranchForm(b);
                              }}
                              title="Edit"
                            >
                              <PencilSquare size={14} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="rounded-3"
                              onClick={() => handleDeleteBranch(b.id)}
                              title="Delete"
                            >
                              <Trash size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <hr />
              <h5 className="mb-3 fw-bold">
                {editingBranch ? "Edit Branch" : "Add Branch"}
              </h5>
              {branchError && (
                <Alert variant="danger" className="mb-3 rounded-3">
                  {branchError}
                </Alert>
              )}

              <Form onSubmit={handleBranchSubmit}>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      Branch Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="name"
                      value={branchForm.name}
                      onChange={handleBranchChange}
                      required
                      className="rounded-3"
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="city"
                      value={branchForm.city}
                      onChange={handleBranchChange}
                      required
                      className="rounded-3"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      State <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="state"
                      value={branchForm.state}
                      onChange={handleBranchChange}
                      required
                      className="rounded-3"
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label className="fw-bold">
                      Country <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="country"
                      value={branchForm.country}
                      onChange={handleBranchChange}
                      required
                      className="rounded-3"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      name="address"
                      value={branchForm.address}
                      onChange={handleBranchChange}
                      className="rounded-3"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <PhoneInput
                      country={"in"}
                      value={branchForm.phone}
                      onChange={(value) =>
                        setBranchForm((prev) => ({
                          ...prev,
                          phone: value ? `+${value}` : "",
                        }))
                      }
                      inputProps={{
                        name: "phone",
                        className:
                          "form-control manage-company-phone-input rounded-3",
                      }}
                      enableSearch={true}
                      containerClass="manage-company-phone-container"
                      inputClass="manage-company-phone-input"
                      buttonClass="manage-company-phone-button"
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Landmark</Form.Label>
                    <Form.Control
                      name="landmark"
                      value={branchForm.landmark}
                      onChange={handleBranchChange}
                      className="rounded-3"
                    />
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="rounded-3"
                    onClick={resetBranchForm}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    className="rounded-3"
                    disabled={branchLoading}
                  >
                    {branchLoading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Saving...
                      </>
                    ) : editingBranch ? (
                      "Update Branch"
                    ) : (
                      "Add Branch"
                    )}
                  </Button>
                </div>
              </Form>
            </>
          ) : (
            <p>No company selected.</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showServiceModal}
        onHide={() => setShowServiceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Gear className="me-2" /> Manage Services — {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany ? (
            <ManageServices companyId={selectedCompany.id} />
          ) : (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />{" "}
              Loading...
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showEmployeeModal}
        onHide={() => setShowEmployeeModal(false)}
        fullscreen={true} // Makes the modal full-screen
        // size="lg"
        // centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <People className="me-2" /> Manage Employees —{" "}
            {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany ? (
            <ManageEmployees companyId={selectedCompany.id} />
          ) : (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />{" "}
              Loading...
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <PencilSquare className="me-2" /> Edit Company —{" "}
            {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" className="mb-3 rounded-3">
              {editError}
            </Alert>
          )}
          <Form onSubmit={handleUpdateCompany}>
            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Company Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  required
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Owner Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="owner_name"
                  value={editForm.owner_name}
                  onChange={handleEditChange}
                  required
                  className="rounded-3"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="company_email"
                  value={editForm.company_email}
                  onChange={handleEditChange}
                  type="email"
                  required
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  GST Number 
                </Form.Label>
                {/* <span className="text-danger">*</span> required */}
                <Form.Control
                  name="gst_number"
                  value={editForm.gst_number}
                  onChange={handleEditChange}
                  pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}"
                  title="Enter a valid GST number (e.g., 22AAAAA0000A1Z5)"
                  className="rounded-3"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Phone <span className="text-danger">*</span>
                </Form.Label>
                <PhoneInput
                  country={"in"}
                  value={editForm.phone}
                  onChange={(value) => {
                    setEditForm((prev) => ({
                      ...prev,
                      phone: value ? `+${value}` : "+91",
                    }));
                  }}
                  inputProps={{
                    name: "phone",
                    required: true,
                    className:
                      "form-control manage-company-phone-input rounded-3",
                  }}
                  enableSearch={true}
                  containerClass="manage-company-phone-container"
                  inputClass="manage-company-phone-input"
                  buttonClass="manage-company-phone-button"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  name="website"
                  value={editForm.website}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Established Year</Form.Label>
                <Form.Control
                  type="number"
                  name="estd"
                  value={editForm.estd}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>PAN Number</Form.Label>
                <Form.Control
                  name="pan_number"
                  value={editForm.pan_number}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Company Logo</Form.Label>
                <Form.Control
                  type="file"
                  name="logo"
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="category_id"
                  value={editForm.category_id || ""}
                  onChange={handleEditChange}
                  required
                  className="rounded-3"
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Subcategory <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="subcategory_id"
                  value={editForm.subcategory_id || ""}
                  onChange={handleEditChange}
                  disabled={!editForm.category_id}
                  required
                  className="rounded-3"
                >
                  <option value="">Select Subcategory</option>
                  {subcategories.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold d-flex align-items-center">
                  Accept Online Payments
                  <Form.Check
                    type="switch"
                    id="accept-online-payments"
                    className="ms-3"
                    checked={acceptPayments}
                    onChange={handleToggleChange}
                  />
                </Form.Label>
                <small className="text-muted">
                  Enable to receive payments through TickTickGo. <br/>A platform fee
                  + gateway charges will apply.
                </small>
              </Col>
            </Row>
                          {/* === CHECK-IN & CHECK-OUT TIME (Only for hotel/motel categories) === */}
              {(() => {
                const DATE_RANGE_CATEGORIES = {
                  5: [38, 39, 36],
                  6: [41, 42],
                  10: [72, 67, 68, 69, 71],
                  7: [52, 47, 48, 49, 50, 54],
                  9: [64],
                  12: [78],
                  13: [81, 83, 84],
                  4: [32, 34],
                  1: [1, 8],
                };

                const isHotelCategory = DATE_RANGE_CATEGORIES[editForm.category_id]?.includes(
                  parseInt(editForm.subcategory_id)
                );

                if (!isHotelCategory) return null;

                return (
                  <Row>
                    <Col xs={12} md={6} className="mb-3">
                      <Form.Label className="fw-bold">
                        Check-in Time <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="time"
                        name="check_in_time"
                        value={editForm.check_in_time || "14:00"}
                        onChange={handleEditChange}
                        required
                        className="rounded-3"
                      />
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                      <Form.Label className="fw-bold">
                        Check-out Time <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="time"
                        name="check_out_time"
                        value={editForm.check_out_time || "12:00"}
                        onChange={handleEditChange}
                        required
                        className="rounded-3"
                      />
                    </Col>
                  </Row>
                );
              })()}
            {/* ---------- NEW TOGGLE (place wherever you like) ---------- */}

            <hr />
            <h6 className="fw-bold mb-3">Address Details</h6>
            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  name="address"
                  value={editForm.address}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Landmark</Form.Label>
                <Form.Control
                  name="landmark"
                  value={editForm.landmark}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  name="city"
                  value={editForm.city}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label>State</Form.Label>
                <Form.Control
                  name="state"
                  value={editForm.state}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  name="country"
                  value={editForm.country}
                  onChange={handleEditChange}
                  className="rounded-3"
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-3 px-3"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCardId(null); // Clear selected card
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                className="rounded-3 px-3"
              >
                Update
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showQRModal}
        onHide={() => {
          setShowQRModal(false);
          setSelectedCardId(null); // Clear selected card
        }}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>QR Code — {selectedCompany?.slug}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center position-relative">
          <QRCodeSVG
            ref={qrCodeRef}
            value={`https://ticktickgo.in/company/${selectedCompany?.slug}`}
            size={150}
            fgColor="#0066cc"
            imageSettings={{
              src: "/favicon-32x32.png",
              x: null,
              y: null,
              height: 30,
              width: 30,
              excavate: true,
            }}
            style={{ display: "block", margin: "0 auto" }}
          />
          <img
            src="/logo.png"
            alt="Footer Logo"
            style={{
              marginTop: "10px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              height: "30px",
            }}
          />
          <Button
            onClick={handleDownload}
            variant="primary"
            size="sm"
            className="mt-3 rounded-3 px-3"
          >
            Download QR Code
          </Button>
        </Modal.Body>
      </Modal>

      <Modal
        show={showSubscribeModal}
        onHide={() => setShowSubscribeModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <CreditCard className="me-2" />
            Subscribe/Renew — {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* INTERNATIONAL TOGGLE */}
          <div className="mb-4 p-3 border rounded bg-light">
            <Form.Check
              type="switch"
              id="international-switch"
              label="Are you an International Client?"
              checked={isInternational}
              onChange={(e) => setIsInternational(e.target.checked)}
            />
            <small className="text-muted">
              {isInternational
                ? "Payment via Stripe in USD (88 INR = 1 USD)"
                : "Payment via Razorpay in INR"}
            </small>
          </div>

          {/* PACKAGE SELECT */}
          <Form.Group className="mb-3">
            <Form.Label>Select Package</Form.Label>
            <Form.Select
              value={selectedPackageId}
              onChange={(e) => setSelectedPackageId(e.target.value)}
            >
              <option value="">Choose...</option>
              {packages.map((pkg) => {
                const totalInr = Number(pkg.total_amount);
                const totalUsd = (totalInr / 88).toFixed(2);
                return (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} —{" "}
                    {isInternational
                      ? `$${totalUsd} USD`
                      : `₹${totalInr.toFixed(2)} INR`}
                    {isInternational && ` (≈ ₹${totalInr.toFixed(2)})`}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>

          {/* PRICE BREAKDOWN */}
          {selectedPackageId && (
            <Alert variant={isInternational ? "info" : "light"}>
              {(() => {
                const pkg = packages.find(
                  (p) => p.id === parseInt(selectedPackageId)
                );
                const inr = Number(pkg.total_amount);
                const usd = (inr / 88).toFixed(2);
                return (
                  <>
                    <strong>
                      {isInternational
                        ? `Pay: $${usd} USD`
                        : `Pay: ₹${inr.toFixed(2)} INR`}
                    </strong>
                    {isInternational && (
                      <small className="d-block">
                        ≈ ₹{inr.toFixed(2)} @ 88 INR/USD
                      </small>
                    )}
                    <hr />
                    <div>
                      <small>Base: ₹{Number(pkg.price).toFixed(2)}</small>
                      <br />
                      <small>
                        Tax ({pkg.tax_percent}%): ₹
                        {Number(pkg.tax_amount).toFixed(2)}
                      </small>
                    </div>
                  </>
                );
              })()}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSubscribeModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant={isInternational ? "info" : "primary"}
            onClick={isInternational ? handleStripeSubscribe : handleSubscribe}
            disabled={subscribeLoading || !selectedPackageId}
          >
            {subscribeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Processing...
              </>
            ) : (
              <>
                {isInternational ? (
                  <>
                    <svg
                      width="16"
                      height="16"
                      className="me-1"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                    Pay with Stripe
                  </>
                ) : (
                  "Pay with Razorpay"
                )}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInventoryModal}
        onHide={closeInventoryModal}
        fullscreen={true} // Fullscreen on all devices
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h5">
            Inventory — {selectedCompany?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedCompany ? (
            <InventoryManager
              companyId={selectedCompany.id}
              companyName={selectedCompany?.name}
            />
          ) : (
            <div className="p-5 text-center">
              <Spinner animation="border" size="sm" /> Loading inventory...
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={showHistoryModal}
        onHide={() => {
          setShowHistoryModal(false);
          setSelectedCardId(null); // Clear selected card
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Payment History for {selectedCompany?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Package</th>
                  <th>Amount (INR)</th>
                  <th>Status</th>
                  <th>Expiration</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.length > 0 ? (
                  paymentHistory.map((pay) => (
                    <tr key={pay.id}>
                      <td>{new Date(pay.payment_date).toLocaleString()}</td>
                      <td>{pay.package_name}</td>
                      <td>₹{pay.amount / 100}</td>
                      <td>{pay.status}</td>
                      <td>
                        {pay.expiration_date
                          ? new Date(pay.expiration_date).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No payment history
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            className="rounded-3"
            onClick={() => {
              setShowHistoryModal(false);
              setSelectedCardId(null); // Clear selected card
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {showVerifyModal && (
        <VerifyBusiness
          companyId={selectedCompany?.id}
          onClose={() => {
            setShowVerifyModal(false);
            setSelectedCompany(null);
            setSelectedCardId(null); // Clear selected card
            fetchCompanies();
          }}
        />
      )}
      {/* === NEW ATTENDANCE MODAL === */}
      <AttendanceModal
        show={showAttendanceModal}
        onHide={() => {
          setShowAttendanceModal(false);
          setSelectedCardId(null);
        }}
        companyId={selectedCompany?.id}
        companyName={selectedCompany?.name}
      />
    </div>
  );
};

export default ManageCompany;
